const THAI_MONTHS_FULL = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];

const THAI_MONTHS_SHORT = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];

/**
 * แปลงวันที่ ISO (YYYY-MM-DD) เป็นรูปแบบภาษาไทย ปี พ.ศ.
 * @param dateStr วันที่ในรูปแบบ 'YYYY-MM-DD'
 * @param formatType 'full' = 13 กันยายน 2569, 'short' = 13 ก.ย. 2569, 'numeric' = 13/09/2569
 */
export function formatThaiDate(
  dateStr: string | null | undefined,
  formatType: 'full' | 'short' | 'numeric' = 'full'
): string {
  if (!dateStr) return '-';

  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;

  const year = parseInt(parts[0], 10);
  const monthIdx = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);

  if (isNaN(year) || isNaN(monthIdx) || isNaN(day) || monthIdx < 0 || monthIdx > 11) {
    return dateStr;
  }

  const thaiYear = year + 543;

  if (formatType === 'numeric') {
    const padDay = String(day).padStart(2, '0');
    const padMonth = String(monthIdx + 1).padStart(2, '0');
    return `${padDay}/${padMonth}/${thaiYear}`;
  }

  if (formatType === 'short') {
    return `${day} ${THAI_MONTHS_SHORT[monthIdx]} ${thaiYear}`;
  }

  return `${day} ${THAI_MONTHS_FULL[monthIdx]} ${thaiYear}`;
}

/**
 * แปลงช่วงวันที่เป็นรูปแบบภาษาไทย ปี พ.ศ.
 * - หากเป็นเดือนเดียวกัน ปีเดียวกัน: เช่น "13-17 กันยายน 2569"
 * - หากเป็นวันเดียวกัน: เช่น "13 กันยายน 2569"
 * - หากคนละเดือน ปีเดียวกัน: เช่น "28 สิงหาคม - 5 กันยายน 2569"
 * - หากคนละปี: เช่น "28 ธันวาคม 2568 - 5 มกราคม 2569"
 */
export function formatThaiDateRange(startDate: string, endDate: string): string {
  if (!startDate || !endDate) return '-';
  if (startDate === endDate) return formatThaiDate(startDate, 'full');

  const startParts = startDate.split('-');
  const endParts = endDate.split('-');
  if (startParts.length !== 3 || endParts.length !== 3) {
    return `${formatThaiDate(startDate, 'full')} - ${formatThaiDate(endDate, 'full')}`;
  }

  const startYear = parseInt(startParts[0], 10);
  const startMonth = parseInt(startParts[1], 10) - 1;
  const startDay = parseInt(startParts[2], 10);

  const endYear = parseInt(endParts[0], 10);
  const endMonth = parseInt(endParts[1], 10) - 1;
  const endDay = parseInt(endParts[2], 10);

  const thaiStartYear = startYear + 543;
  const thaiEndYear = endYear + 543;

  // กรณีปีเดียวกัน และเดือนเดียวกัน -> เช่น "13-17 กันยายน 2569"
  if (startYear === endYear && startMonth === endMonth) {
    return `${startDay}-${endDay} ${THAI_MONTHS_FULL[startMonth]} ${thaiEndYear}`;
  }

  // กรณีปีเดียวกัน แต่คนละเดือน -> เช่น "28 สิงหาคม - 5 กันยายน 2569"
  if (startYear === endYear) {
    return `${startDay} ${THAI_MONTHS_FULL[startMonth]} - ${endDay} ${THAI_MONTHS_FULL[endMonth]} ${thaiEndYear}`;
  }

  // กรณีคนละปี -> เช่น "28 ธันวาคม 2568 - 5 มกราคม 2569"
  return `${startDay} ${THAI_MONTHS_FULL[startMonth]} ${thaiStartYear} - ${endDay} ${THAI_MONTHS_FULL[endMonth]} ${thaiEndYear}`;
}

/**
 * สร้างชื่อเรื่องรายงานตามข้อกำหนด ส่วนที่ 1:
 * "รายงานปริมาณการรับ-ใช้ และยอดคงเหลือ{สารเคมี} ประจำวันที่ dd mmm yyyy - dd mmm yyyy (ภาษาไทย ปี พ.ศ.)"
 * ในกรณีเป็นเดือนเดียวกัน จะแสดงผลเป็น เช่น "13-17 กันยายน 2569"
 */
export function formatReportTitle(
  startDate: string,
  endDate: string,
  chemicalName?: string
): string {
  const rangeText = formatThaiDateRange(startDate, endDate);
  const chemText = chemicalName ? chemicalName : 'สารเคมี';

  return `รายงานปริมาณการรับ-ใช้ และยอดคงเหลือ${chemText} ประจำวันที่ ${rangeText}`;
}

